import { useCallback, useState } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { useOrganization } from '../contexts/OrganizationContext';
import { apiEndpoints } from '../utils/api';
import { Finding, FindingStatus, FindingStatusUpdateRequest } from '../types/finding';
import { isClosingStatus } from '../utils/findingUtils';
import { FINDING_STATUS_LABEL } from '../design-system/severity';
import toast from '../design-system/toast';

/** `error_code` the API returns when the organization requires a reason and none was sent. */
const REASON_REQUIRED_ERROR_CODE = 'FINDING_CLOSURE_REASON_REQUIRED';

/** A status change that is waiting for the user to give a reason. */
export interface PendingStatusReason {
  finding: Finding;
  status: FindingStatus;
}

export interface FindingStatusReasonPrompt {
  /** The change awaiting a reason, or null when no prompt is open. */
  pending: PendingStatusReason | null;
  isSubmitting: boolean;
  /** Failure from the last submit, shown in the prompt so the typed reason is kept. */
  error: string | null;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

interface UseFindingStatusUpdateResult {
  /** Change a finding's status, asking for a reason first when the organization requires one. */
  updateStatus: (finding: Finding, status: FindingStatus) => void;
  /** Id of the finding whose status change is in flight. */
  updatingFindingId: string | null;
  /** State and handlers for `FindingStatusReasonModal`. */
  reasonPrompt: FindingStatusReasonPrompt;
}

/**
 * Status changes for a finding, shared by the findings list and the finding page.
 *
 * Organizations can require a reason to close a finding without a fix (resolved,
 * ignored, false positive). When they do, the change is held until the user supplies
 * one through `reasonPrompt`.
 */
export function useFindingStatusUpdate(onUpdated: (finding: Finding) => void): UseFindingStatusUpdateResult {
  const api = useAuthenticatedApi();
  const { currentOrganization } = useOrganization();

  const [updatingFindingId, setUpdatingFindingId] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingStatusReason | null>(null);
  const [promptError, setPromptError] = useState<string | null>(null);
  // Organizations are loaded once per session, so the cached setting can be stale when
  // an owner turns it on elsewhere. Once the server says a reason is needed, believe it.
  const [serverRequiresReason, setServerRequiresReason] = useState(false);

  const reasonRequired = Boolean(currentOrganization?.require_finding_closure_reason) || serverRequiresReason;

  const submit = useCallback(
    async (finding: Finding, status: FindingStatus, reason?: string) => {
      setUpdatingFindingId(finding.id);
      setPromptError(null);
      try {
        const payload: FindingStatusUpdateRequest = reason ? { status, reason } : { status };
        const updated = await api.patch<Finding>(apiEndpoints.findings.updateStatus(finding.id), payload);
        setUpdatingFindingId(null);
        setPending(null);
        onUpdated(updated);
        toast.success(`Finding marked as ${FINDING_STATUS_LABEL[status].toLowerCase()}`);
      } catch (error: any) {
        console.error('Error updating status:', error);
        // Cleared before the prompt is opened below, so it never appears mid-"saving".
        setUpdatingFindingId(null);
        const message = error?.message || 'Failed to update status';
        if (error?.data?.detail?.error_code === REASON_REQUIRED_ERROR_CODE) {
          // Ask for the reason instead of failing; the user did nothing wrong.
          setServerRequiresReason(true);
          setPending({ finding, status });
          if (reason !== undefined) setPromptError(message);
        } else if (reason !== undefined) {
          setPromptError(message);
        } else {
          toast.error(message);
        }
      }
    },
    [api, onUpdated],
  );

  const updateStatus = useCallback(
    (finding: Finding, status: FindingStatus) => {
      if (reasonRequired && isClosingStatus(status)) {
        setPromptError(null);
        setPending({ finding, status });
        return;
      }
      void submit(finding, status);
    },
    [reasonRequired, submit],
  );

  const onConfirm = useCallback(
    (reason: string) => {
      const trimmed = reason.trim();
      if (!pending || !trimmed) return;
      void submit(pending.finding, pending.status, trimmed);
    },
    [pending, submit],
  );

  const onCancel = useCallback(() => {
    setPending(null);
    setPromptError(null);
  }, []);

  return {
    updateStatus,
    updatingFindingId,
    reasonPrompt: {
      pending,
      isSubmitting: pending !== null && updatingFindingId === pending.finding.id,
      error: promptError,
      onConfirm,
      onCancel,
    },
  };
}
